import React from "react";
import { useMutation } from "@apollo/client";
import { ApolloError, gql } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { Button } from "src/components/button/button";
import { Input } from "src/components/input/input";
import { UpdateUserInput, UpdateUserPayload } from "src/generated/types";
import { DELETE_COOKIE_REQUEST } from "src/state/ducks/auth";
import { parseError } from "src/utils/parse_error";
import { Routes } from "src/utils/routes";

/*
 * GraphQL
 */

const CHANGE_PASSWORD_MUTATION = gql`
    mutation ChangePassword($input: UpdateUserInput!) {
        updateUser(input: $input) {
            user {
                id
            }
        }
    }
`;

/*
 * Types
 */

interface ChangePasswordFormProps {
    userId?: string;
}

interface ApolloData {
    updateUser: UpdateUserPayload;
}

interface ApolloMutationVars {
    input: UpdateUserInput;
}

interface FormValues {
    oldPassword: string;
    newPassword: string;
    newPasswordConfirm: string;
}

/*
 * Main
 */

export const ChangePasswordForm = (props: ChangePasswordFormProps): React.ReactElement => {
    const { userId = "some_user_id" } = props;

    const dispatch = useDispatch();
    const history = useHistory();
    const [changePassword, { client, loading }] = useMutation<ApolloData, ApolloMutationVars>(CHANGE_PASSWORD_MUTATION);

    const initialValues: FormValues = {
        oldPassword: "",
        newPassword: "",
        newPasswordConfirm: "",
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, { resetForm, setSubmitting }): void => {
                changePassword({
                    variables: { input: { id: userId, fields: { password: values.newPassword } } },
                })
                    .then(async () => {
                        toast.success("Password successfully changed.");
                        dispatch({
                            type: DELETE_COOKIE_REQUEST,
                        });
                        history.push(Routes.auth);
                        await client?.clearStore();
                    })
                    .catch((error: ApolloError) => {
                        toast.error(parseError(error));
                    });

                setSubmitting(false);
                resetForm();
            }}
            validationSchema={Yup.object().shape({
                oldPassword: Yup.string().required("Required"),
                newPassword: Yup.string()
                    .matches(
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
                        "Must Contain 8 Characters, One Uppercase, One Lowercase",
                    )
                    .required("Required"),
                newPasswordConfirm: Yup.string()
                    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
                    .required("Required"),
            })}
        >
            {(formik: FormikProps<FormValues>) => (
                <Form>
                    <Input autoComplete="username" id="username" name="username" style={{ display: "none" }} />
                    <Input
                        autoComplete="current-password"
                        id="oldPassword"
                        label="Current Password"
                        name="oldPassword"
                        placeholder="Enter Your Old Password"
                        type="password"
                    />
                    <Input
                        autoComplete="new-password"
                        id="newPassword"
                        label="New Password"
                        name="newPassword"
                        placeholder="Enter Your New Password"
                        type="password"
                    />
                    <Input
                        autoComplete="new-password"
                        id="newPasswordConfirm"
                        label="Confirm Password"
                        name="newPasswordConfirm"
                        placeholder="Confirm Your New Password"
                        type="password"
                    />
                    <Button disabled={formik.isSubmitting || !formik.isValid || loading} type="submit">
                        Update Password
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
